import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: 'What does collectivism value most?',
    options: ['Family and community', 'Individual achievement', 'Material wealth', 'Political power', 'None of the above'],
    correctAnswer: 0,
    explanation: 'Collectivism values family and community over self.'
  },
  {
    question: 'Power distance refers to:',
    options: ['Respecting hierarchy and authority', 'Avoiding rules', 'Ignoring elders', 'Equality in all things', 'None of the above'],
    correctAnswer: 0,
    explanation: 'Power distance is about respecting hierarchy and authority.'
  },
  {
    question: 'Uncertainty avoidance means:',
    options: ['Preference for order and stability', 'Seeking chaos', 'Ignoring tradition', 'Always taking risks', 'None of the above'],
    correctAnswer: 0,
    explanation: 'Uncertainty avoidance is a preference for order and stability.'
  },
  {
    question: 'Masculinity vs. femininity in culture is about:',
    options: ['Balance of strength and care', 'Only strength', 'Only care', 'Ignoring gender roles', 'None of the above'],
    correctAnswer: 0,
    explanation: 'It is about balancing strength and care.'
  },
  {
    question: 'Long-term orientation means:',
    options: ['Future planning and perseverance', 'Living only for today', 'Ignoring the future', 'Material success', 'None of the above'],
    correctAnswer: 0,
    explanation: 'Long-term orientation is about future planning and perseverance.'
  }
];

const ReligioTraditioCulturalModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('rtcM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  if (isLoading) {
    return <div className="module-form"><p>Loading quiz...</p></div>;
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">Congratulations!</h2>
        <p className="completion-subtitle">You've completed the Module 3 quiz!</p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">Key Achievements</h3>
            <ul className="achievement-list">
              <li><span className="check"></span>Understanding cultural values</li>
              <li><span className="check"></span>CVSCALE dimensions</li>
              <li><span className="check"></span>Reflection on Filipino traits</li>
            </ul>
          </div>
          <button className="resource-button" onClick={() => navigate('/panel/learn/religio-traditio-cultural-resources')}>Return to Resources</button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left religio-traditional-cultural-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Cultural Identity and Spiritual Expression</p>
            <hr className="module-divider" />
            <p className="module-tool-info">Lecture + reflection + journaling + community sharing</p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">Question {currentQuestion + 1} of {quizQuestions.length}</span>
            </div>
            <div className="question-section">
              <h2 className="question-title">{currentQuizQuestion.question}</h2>
              <div className="answer-options">
                {currentQuizQuestion.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuizQuestion.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuizQuestion.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button className="btn-secondary" onClick={currentQuestion === 0 ? () => navigate('/panel/learn/religio-traditio-cultural-resources') : handleBack}>
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Next Question</button>
              ) : (
                <button className="btn-primary" onClick={handleNext} disabled={selectedAnswer === null}>Complete Quiz</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule3QuizPage;


